import React, { useCallback } from 'react';
import { Autocomplete, AutocompleteProps, Box } from '@mui/material';
import type { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';

import { getMaterialColor } from '../../../helpers';
import { ColorDot } from '../ColorDot';
import { FieldSurface, SurfaceTextField } from '../FormField';

export interface MaterialAutocompleteProps
  extends Omit<
    AutocompleteProps<string, false, false, false>,
    'renderInput' | 'renderOption'
  > {
  inputRef?: React.Ref<HTMLInputElement>;
  inputName?: string;
  onInputBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  surface?: FieldSurface;
}

const indicatorStyles = {
  display: 'inline-flex',
  alignItems: 'center',
  flexShrink: 0,
};
const selectedIndicatorStyles = indicatorStyles;
const optionStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  minWidth: 0,
};
const getDefaultOptionLabel = (option: string) => option;

const getMaterialCssColor = (material: string) => {
  const [red, green, blue] = getMaterialColor(material.trim().toLowerCase());

  return `rgb(${red}, ${green}, ${blue})`;
};

const MaterialIndicator = ({ material }: { material: string }) => (
  <Box component="span" data-material-indicator={material} sx={indicatorStyles}>
    <ColorDot color={getMaterialCssColor(material)} />
  </Box>
);

const MaterialAutocomplete = React.memo(
  ({
    getOptionLabel = getDefaultOptionLabel,
    inputName,
    inputRef,
    onInputBlur,
    size = 'small',
    surface = 'sunken',
    value,
    ...props
  }: MaterialAutocompleteProps) => {
    const selectedMaterial = typeof value === 'string' ? value : null;
    const renderOption = useCallback(
      (optionProps: React.HTMLAttributes<HTMLLIElement>, option: string) => (
        <li {...optionProps}>
          <Box component="span" sx={optionStyles}>
            <MaterialIndicator material={option} />
            <span>{getOptionLabel(option)}</span>
          </Box>
        </li>
      ),
      [getOptionLabel]
    );
    const renderInput = useCallback(
      (params: AutocompleteRenderInputParams) => (
        <SurfaceTextField
          {...params}
          inputRef={inputRef}
          name={inputName}
          onBlur={onInputBlur}
          size={size}
          surface={surface}
          InputProps={{
            ...params.InputProps,
            startAdornment: selectedMaterial ? (
              <Box
                component="span"
                data-selected-material-indicator
                sx={selectedIndicatorStyles}
              >
                <MaterialIndicator material={selectedMaterial} />
              </Box>
            ) : (
              params.InputProps.startAdornment
            ),
          }}
        />
      ),
      [inputName, inputRef, onInputBlur, selectedMaterial, size, surface]
    );

    return (
      <Autocomplete
        {...props}
        getOptionLabel={getOptionLabel}
        renderInput={renderInput}
        renderOption={renderOption}
        size={size}
        value={value}
      />
    );
  }
);

MaterialAutocomplete.displayName = 'MaterialAutocomplete';

export { MaterialAutocomplete, MaterialIndicator };
